import React from 'react';
import { useSelector } from 'react-redux';
import {
    useTranslator,
    useCurrentLanguage,
    useUrlLocalizer
} from 'tradera/lang/translate';
import {
    areLanguageCodesIso2Equal,
    isSwedishOrUndefined
} from 'tradera/localization/languagecode';
import ConsentService from 'tradera/utils/consent-service';
import { useIsFeatureEnabled } from 'tradera/hooks/use-is-feature-enabled';
import styles from './footer-links.module.scss';
import { selectAvailableLanguageCodesIso2 } from 'tradera/state/language/selectors';

import AppstoreLogo from '@tradera/blueprint/theme/logos/appstore.svg?next-image';
import FacebookIcon from '@tradera/blueprint/theme/icons2/network-fb.svg?next-image';
import GooglePlayLogo from '@tradera/blueprint/theme/logos/googleplay.svg?next-image';
import InstagramIcon from '@tradera/blueprint/theme/icons2/network-ig.svg?next-image';
import LinkedInIcon from '@tradera/blueprint/theme/icons2/network-linkedin.svg?next-image';
import TikTokIcon from '@tradera/blueprint/theme/icons2/network-tt.svg?next-image';
import BankidIcon from '@tradera/blueprint/theme/icons2/bankid.svg?next-image';
import { MitIdLogo } from 'tradera/components/international-identification-modal/mit-id-logo';
import { useAppDownloadLink } from 'tradera/hooks/use-app-download-link';

const FooterLinks = ({ links, callerId }) => {
    const { language } = useCurrentLanguage();

    const linksToRender = links.filter((link) =>
        link.includedLanguages.find((lang) =>
            areLanguageCodesIso2Equal(lang, language)
        )
    );

    return (
        <>
            {linksToRender.map(
                (
                    { text, className, includedLanguages, href, icon, ...rest },
                    index
                ) => {
                    return (
                        <li
                            key={`${callerId}-${index}`}
                            className={`pb-1  pr-2 ${
                                className !== undefined ? className : ''
                            }`}>
                            <a
                                {...rest}
                                href={href[language.toUpperCase()] || href}>
                                {icon && icon}
                                {text}
                            </a>
                        </li>
                    );
                }
            )}
        </>
    );
};

export const BuyingLinks = () => {
    const { t } = useTranslator();
    const { toLocalizedUrl } = useUrlLocalizer();
    const availableLanguageCodesIso2 = useSelector(
        selectAvailableLanguageCodesIso2
    );

    const links = [
        {
            href: toLocalizedUrl('/how-to-buy'),
            'data-ga-category': 'Footer',
            'data-ga-action': 'Navigation',
            'data-ga-label': 'Så handlar du på Tradera',
            text: t('footer_howToShop'),
            includedLanguages: availableLanguageCodesIso2
        },
        {
            href: toLocalizedUrl('/kategorier'),
            'data-ga-category': 'Footer',
            'data-ga-action': 'Navigation',
            'data-ga-label': 'Kategorier',
            text: t('footer_categories'),
            includedLanguages: availableLanguageCodesIso2
        },
        {
            href: toLocalizedUrl('/brands'),
            'data-ga-category': 'Footer',
            'data-ga-action': 'Navigation',
            'data-ga-label': 'Populära varumärken',
            text: t('footer_popularBrands'),
            includedLanguages: availableLanguageCodesIso2
        },
        {
            href: {
                SV: '/inspiration',
                DA: '/info/da/inspiration'
            },
            'data-ga-category': 'Footer',
            'data-ga-action': 'Navigation',
            'data-ga-label': 'Inspiration',
            text: t('footer_inspiration'),
            includedLanguages: ['SV', 'DA']
        },
        {
            href: '/info/gronfrakt/',
            'data-ga-category': 'Footer',
            'data-ga-action': 'Navigation',
            'data-ga-label': 'Grön frakt på Tradera',
            text: t('footer_greenShipping'),
            includedLanguages: ['SV']
        }
    ];

    return (
        <div className={`${styles.linkContainer} col-6 col-md-3 mx-md-0 mb-3`}>
            <h4 className="text-styled">{t('footer_buyHeading')}</h4>
            <ul className="plain-list">
                <FooterLinks callerId="buyinglinks" links={links} />
            </ul>
        </div>
    );
};

export const SellingLinks = () => {
    const { t } = useTranslator();
    const { toLocalizedUrl } = useUrlLocalizer();
    const isPennybridgeCharitiesEnabled = useIsFeatureEnabled(
        'pennybridge-charities'
    );
    const availableLanguageCodesIso2 = useSelector(
        selectAvailableLanguageCodesIso2
    );

    const links = [
        {
            href: toLocalizedUrl('/how-to-sell'),
            'data-ga-category': 'Footer',
            'data-ga-action': 'Navigation',
            'data-ga-label': 'Så säljer du som privatperson',
            text: t('footer_howToSellPrivate'),
            includedLanguages: availableLanguageCodesIso2
        },
        {
            href: {
                SV: 'https://info.tradera.com/foretag/',
                DA: 'https://info.tradera.com/da/virksomhedskonto/'
            },
            'data-ga-category': 'Footer',
            'data-ga-action': 'Navigation',
            'data-ga-label': 'Sälj som företag',
            text: t('footer_howToSellCorporate'),
            includedLanguages: ['SV', 'DA']
        },
        isPennybridgeCharitiesEnabled && {
            href: toLocalizedUrl('/causes'),
            'data-ga-category': 'Footer',
            'data-ga-action': 'Navigation',
            'data-ga-label': 'Donera med Tradera',
            text: t('footer_donateWithTradera'),
            includedLanguages: ['SV']
        },
        {
            href: toLocalizedUrl('/frakt'),
            'data-ga-category': 'Footer',
            'data-ga-action': 'Navigation',
            'data-ga-label': 'Fraktkalkylator',
            text: t('footer_shippingCalculator'),
            includedLanguages: ['SV', 'DA', 'EN']
        },
        {
            href: {
                SV: 'https://info.tradera.com/butik-privat/',
                DA: 'https://info.tradera.com/da/butik-privat/'
            },
            'data-ga-category': 'Footer',
            'data-ga-action': 'Navigation',
            'data-ga-label': 'Butik',
            text: t('footer_store'),
            includedLanguages: ['SV', 'DA']
        },
        {
            href: {
                SV: 'https://info.tradera.com/salja-pa-tradera/prislista-privat/',
                DA: 'https://info.tradera.com/da/price-list-personal-account/',
                DE: 'https://info.tradera.com/en/price-list-personal-account/',
                EN: 'https://info.tradera.com/en/price-list-personal-account/'
            },
            'data-ga-category': 'Footer',
            'data-ga-action': 'Navigation',
            'data-ga-label': 'Priser',
            text: t('footer_prices'),
            includedLanguages: ['SV', 'DA', 'EN']
        },
        {
            href: {
                SV: 'https://prolister.tradera.com/'
            },
            'data-ga-category': 'Footer',
            'data-ga-action': 'Navigation',
            'data-ga-label': 'ProLister',
            text: t('footer_prolister'),
            includedLanguages: ['SV']
        }
    ].filter(Boolean);

    return (
        <div className={`${styles.linkContainer} col-6 col-md-3 mx-md-0 mb-3`}>
            <h4 className="text-styled">{t('footer_sellHeading')}</h4>
            <ul className="plain-list">
                <FooterLinks callerId="sellinglinks" links={links} />
            </ul>
        </div>
    );
};

export const ContactAndHelpLinks = () => {
    const { t } = useTranslator();
    const isQuantcastConsentEnabled = useIsFeatureEnabled('quantcast-consent');
    const availableLanguageCodesIso2 = useSelector(
        selectAvailableLanguageCodesIso2
    );

    const links = [
        {
            href: {
                SV: 'https://info.tradera.com/sakerhetscenter/regler-pa-tradera/',
                DA: 'https://info.tradera.com/da/rules-at-tradera-2/'
            },
            'data-ga-category': 'Footer',
            'data-ga-action': 'Navigation',
            'data-ga-label': 'Regler på Tradera',
            text: t('footer_traderaRules'),
            includedLanguages: ['SV', 'DA']
        },
        {
            href: 'http://www.mynewsdesk.com/se/tradera',
            'data-ga-category': 'Footer',
            'data-ga-action': 'Navigation',
            'data-ga-label': 'Press',
            text: t('footer_press'),
            rel: 'nofollow',
            includedLanguages: ['SV']
        },
        {
            href: {
                SV: 'https://info.tradera.com/sakerhetscenter',
                DA: 'https://info.tradera.com/da/security-center/'
            },
            'data-ga-category': 'Footer',
            'data-ga-action': 'Navigation',
            'data-ga-label': 'Säkerhet',
            text: t('footer_security'),
            includedLanguages: ['SV', 'DA']
        },
        {
            href: {
                SV: 'https://info.tradera.com',
                DA: 'https://info.tradera.com/da',
                DE: 'https://info.tradera.com/en',
                EN: 'https://info.tradera.com/en',
                FR: 'https://info.tradera.com/en',
                NL: 'https://info.tradera.com/en'
            },
            'data-ga-category': 'Footer',
            'data-ga-action': 'Navigation',
            'data-ga-label': 'Vanliga frågor',
            text: t('footer_faq'),
            includedLanguages: availableLanguageCodesIso2
        },
        {
            href: 'https://www.tradera.com/info/blogg/',
            'data-ga-category': 'Footer',
            'data-ga-action': 'Navigation',
            'data-ga-label': 'Vad är nytt',
            text: t('footer_whatsUp'),
            includedLanguages: ['SV']
        },
        {
            href: 'https://info.tradera.com/kontakt/',
            'data-ga-category': 'Footer',
            'data-ga-action': 'Navigation',
            'data-ga-label': 'Kontakta oss',
            text: t('footer_contactUs'),
            includedLanguages: ['SV']
        },
        {
            href: '#',
            'data-ga-category': 'Footer',
            'data-ga-action': 'Navigation',
            'data-ga-label': 'Open GDPR Settings',
            text: t('footer_gdpr_settings'),
            onClick: (event) => {
                event.preventDefault();
                if (isQuantcastConsentEnabled) {
                    ConsentService.openUi();
                }
            },
            includedLanguages: availableLanguageCodesIso2
        }
    ];

    return (
        <div className={`${styles.linkContainer} col-6 col-md-3 mx-md-0 mb-3`}>
            <h4 className="text-styled">{t('footer_contactHelpHeading')}</h4>
            <ul className="plain-list">
                <FooterLinks callerId="contactandhelplinks" links={links} />
            </ul>
        </div>
    );
};

export const InformationLinks = () => {
    const { t } = useTranslator();
    const { language } = useCurrentLanguage();
    const availableLanguageCodesIso2 = useSelector(
        selectAvailableLanguageCodesIso2
    );

    const links = [
        {
            href: isSwedishOrUndefined(language)
                ? 'https://info.tradera.com/anvandaravtal/'
                : 'https://info.tradera.com/en/anvandaravtal-eng/',
            'data-ga-category': 'Footer',
            'data-ga-action': 'Navigation',
            'data-ga-label': 'Användaravtal',
            text: t('footer_userAgreement'),
            includedLanguages: availableLanguageCodesIso2
        },
        {
            href: isSwedishOrUndefined(language)
                ? 'https://info.tradera.com/sakerhetscenter/integritetspolicy/'
                : 'https://info.tradera.com/en/integritetespolicy-eng/',
            'data-ga-category': 'Footer',
            'data-ga-action': 'Navigation',
            'data-ga-label': 'Integritetspolicy',
            text: t('footer_integrityPolicy'),
            includedLanguages: availableLanguageCodesIso2
        },
        {
            href: isSwedishOrUndefined(language)
                ? 'https://info.tradera.com/cookies-webb-beacons-och-liknande-teknik/'
                : 'https://info.tradera.com/en/cookies-webb-beacons-och-liknande-teknik/',
            'data-ga-category': 'Footer',
            'data-ga-action': 'Navigation',
            'data-ga-label': 'Cookies',
            text: t('footer_cookies'),
            includedLanguages: availableLanguageCodesIso2
        },
        {
            href: {
                SV: '/info/om-oss',
                DE: '/info/uber-uns/',
                EN: '/info/about-us/',
                DA: '/info/om-os',
                FR: '/info/about-us/',
                NL: '/info/about-us/'
            },
            'data-ga-category': 'Footer',
            'data-ga-action': 'Navigation',
            'data-ga-label': 'Om oss',
            text: t('footer_aboutTradera'),
            includedLanguages: availableLanguageCodesIso2
        },
        {
            href: 'https://careers.tradera.com/',
            'data-ga-category': 'Footer',
            'data-ga-action': 'Navigation',
            'data-ga-label': 'Jobb',
            text: t('footer_availableJobs'),
            includedLanguages: availableLanguageCodesIso2
        }
    ];

    return (
        <div className={`${styles.linkContainer} col-6 col-md-3 mx-md-0 mb-3`}>
            <h4 className="text-styled">{t('footer_informationHeading')}</h4>
            <ul className="plain-list">
                <FooterLinks callerId="informationlinks" links={links} />
            </ul>
        </div>
    );
};

export const SocialLinks = () => {
    const { t } = useTranslator();
    const availableLanguageCodesIso2 = useSelector(
        selectAvailableLanguageCodesIso2
    );

    const { appStore, googlePlay } = useAppDownloadLink();

    const links = [
        {
            ['aria-label']: 'Tradera Facebook',
            href: 'https://www.facebook.com/Tradera',
            ['data-ga-category']: 'Footer',
            ['data-ga-action']: 'Navigation',
            ['data-ga-label']: 'Sociala medier - Facebook',
            rel: 'nofollow',
            icon: (
                <img
                    src={FacebookIcon}
                    loading="lazy"
                    className="icon icon-md icon-dark"
                />
            ),
            includedLanguages: availableLanguageCodesIso2
        },
        {
            ['aria-label']: 'Tradera TikTok',
            href: 'https://www.tiktok.com/@tradera',
            ['data-ga-category']: 'Footer',
            ['data-ga-action']: 'Navigation',
            ['data-ga-label']: 'Sociala medier - TikTok',
            rel: 'nofollow',
            icon: (
                <img
                    src={TikTokIcon}
                    loading="lazy"
                    className="icon icon-md icon-dark"
                />
            ),
            includedLanguages: availableLanguageCodesIso2
        },
        {
            ['aria-label']: 'Tradera Instagram',
            href: {
                SV: 'https://www.instagram.com/tradera/',
                DE: 'https://www.instagram.com/tradera/',
                EN: 'https://www.instagram.com/tradera/',
                DA: 'https://www.instagram.com/tradera_dk/',
                FR: 'https://www.instagram.com/tradera/',
                NL: 'https://www.instagram.com/tradera/'
            },
            ['data-ga-category']: 'Footer',
            ['data-ga-action']: 'Navigation',
            ['data-ga-label']: 'Sociala medier - Instagram',
            rel: 'nofollow',
            icon: (
                <img
                    src={InstagramIcon}
                    loading="lazy"
                    className="icon icon-md icon-dark"
                />
            ),
            includedLanguages: availableLanguageCodesIso2
        },
        {
            ['aria-label']: 'Tradera LinkedIn',
            href: 'https://www.linkedin.com/company/tradera/',
            ['data-ga-category']: 'Footer',
            ['data-ga-action']: 'Navigation',
            ['data-ga-label']: 'Sociala medier - LinkedIn',
            rel: 'nofollow',
            icon: (
                <img
                    src={LinkedInIcon}
                    loading="lazy"
                    className="icon icon-md icon-dark"
                />
            ),
            includedLanguages: availableLanguageCodesIso2
        },
        {
            ['aria-label']: 'Tradera BankId',
            href: 'https://www.bankid.com/',
            ['data-ga-category']: 'Footer',
            ['data-ga-action']: 'Navigation',
            ['data-ga-label']: 'BankId',
            target: '_blank',
            rel: 'nofollow',
            icon: (
                <img
                    src={BankidIcon}
                    loading="lazy"
                    className="icon icon-md icon-dark"
                />
            ),
            includedLanguages: ['sv']
        },
        {
            ['aria-label']: 'Tradera MitId',
            href: 'https://www.mitid.dk/',
            ['data-ga-category']: 'Footer',
            ['data-ga-action']: 'Navigation',
            ['data-ga-label']: 'MitId',
            target: '_blank',
            rel: 'nofollow',
            icon: <MitIdLogo color="dark" />,
            includedLanguages: ['da']
        }
    ];

    return (
        <div className={`${styles.iconRow} mb-2 py-2 row`}>
            {/* Social media */}
            <div className="col-12 col-md-auto">
                <ul className="d-flex plain-list">
                    <FooterLinks callerId="socialLinks" links={links} />
                </ul>
            </div>
            <span className="border-left border-dark ml-1 mr-3 d-none d-md-inline" />
            {/* App download */}
            <div className="d-flex col-12 col-md-auto mt-2 mt-md-0">
                <a
                    href={appStore}
                    rel="nofollow"
                    className="mr-2"
                    data-ga-category="Footer"
                    data-ga-action="Navigation"
                    data-ga-label="App Store">
                    <img
                        src={AppstoreLogo}
                        loading="lazy"
                        height="20"
                        title={t('footer_downloadFromAppStore', {
                            appName: 'App Store'
                        })}
                        className="icon-dark"
                    />
                </a>
                <a
                    href={googlePlay}
                    rel="nofollow"
                    className="ml-2"
                    data-ga-category="Footer"
                    data-ga-action="Navigation"
                    data-ga-label="App Store">
                    <img
                        src={GooglePlayLogo}
                        loading="lazy"
                        height="20"
                        title={t('footer_downloadFromAppStore', {
                            appName: 'Google Play'
                        })}
                        className="icon-dark"
                    />
                </a>
            </div>
        </div>
    );
};
